import React, { useState, useEffect } from 'react';
import { Lock, Shield, AlertTriangle } from 'lucide-react';

const AccessDenied = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [floatingElements, setFloatingElements] = useState([]);

  useEffect(() => {
    setIsVisible(true);
    
    // Create floating elements for background animation
    const elements = Array.from({ length: 12 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 4 + 2,
      duration: Math.random() * 3 + 2,
      delay: Math.random() * 2
    }));
    setFloatingElements(elements);
  }, []);

  const containerStyle = {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(#050A30,#42006C)',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    position: 'relative',
    overflow: 'hidden',
    padding: '20px'
  };

  const backgroundElementStyle = (element) => ({
    position: 'absolute',
    left: `${element.x}%`,
    top: `${element.y}%`,
    width: `${element.size}px`,
    height: `${element.size}px`,
    background: 'rgba(79, 172, 254, 0.1)',
    borderRadius: '50%',
    animation: `float-${element.id} ${element.duration}s ease-in-out infinite`,
    animationDelay: `${element.delay}s`,
    filter: 'blur(1px)'
  });

  const cardStyle = {
    background: 'rgba(255, 255, 255, 0.08)',
    backdropFilter: 'blur(20px)',
    border: '1px solid rgba(255, 255, 255, 0.15)',
    borderRadius: '24px',
    padding: '60px 50px',
    textAlign: 'center',
    maxWidth: '500px',
    width: '100%',
    boxShadow: `
      0 25px 50px -12px rgba(0, 0, 0, 0.4),
      0 0 0 1px rgba(255, 255, 255, 0.05),
      inset 0 1px 0 rgba(255, 255, 255, 0.1)
    `,
    transform: isVisible ? 'translateY(0) scale(1)' : 'translateY(30px) scale(0.9)',
    opacity: isVisible ? 1 : 0,
    transition: 'all 0.8s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
    position: 'relative',
    zIndex: 10
  };

  const iconContainerStyle = {
    width: '100px',
    height: '100px',
    margin: '0 auto 30px',
    background: 'linear-gradient(135deg, #ff6b6b, #ee5a24)',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    animation: 'pulse 2s ease-in-out infinite'
  };

  const iconStyle = {
    color: 'white',
    width: '48px',
    height: '48px',
    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))'
  };

  const titleStyle = {
    fontSize: '32px',
    fontWeight: '700',
    color: 'white',
    marginBottom: '16px',
    letterSpacing: '-0.02em',
    lineHeight: '1.2'
  };

  const messageStyle = {
    fontSize: '18px',
    color: 'rgba(255, 255, 255, 0.8)',
    lineHeight: '1.6',
    marginBottom: '30px',
    fontWeight: '400'
  };

  const buttonStyle = {
    background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    padding: '14px 32px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: '0 4px 15px rgba(79, 172, 254, 0.4)',
    position: 'relative',
    overflow: 'hidden'
  };

  const decorativeRingStyle = {
    position: 'absolute',
    top: '-10px',
    left: '-10px',
    right: '-10px',
    bottom: '-10px',
    border: '2px solid rgba(79, 172, 254, 0.3)',
    borderRadius: '50%',
    animation: 'rotate 4s linear infinite'
  };

  const secondRingStyle = {
    ...decorativeRingStyle,
    top: '-20px',
    left: '-20px',
    right: '-20px',
    bottom: '-20px',
    border: '1px solid rgba(238, 90, 36, 0.2)',
    animationDirection: 'reverse',
    animationDuration: '6s'
  };

  return (
    <div style={containerStyle}>
      <style>{`
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
        
        @keyframes rotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        ${floatingElements.map(element => `
          @keyframes float-${element.id} {
            0%, 100% { 
              transform: translateY(0px) translateX(0px) rotate(0deg);
              opacity: 0.3;
            }
            33% { 
              transform: translateY(-20px) translateX(10px) rotate(120deg);
              opacity: 0.6;
            }
            66% { 
              transform: translateY(10px) translateX(-10px) rotate(240deg);
              opacity: 0.4;
            }
          }
        `).join('')}
        
        button:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(79, 172, 254, 0.6);
        }
        
        button:active {
          transform: translateY(0);
        }
      `}</style>
      
      {/* Floating background elements */}
      {floatingElements.map(element => (
        <div key={element.id} style={backgroundElementStyle(element)} />
      ))}
      
      <div style={cardStyle}>
        <div style={iconContainerStyle}>
          <div style={decorativeRingStyle}></div>
          <div style={secondRingStyle}></div>
          <Lock style={iconStyle} />
        </div>
        
        <h1 style={titleStyle}>Access Restricted</h1>
        <p style={messageStyle}>
          Access to this digital success room is not available to you. Please contact your administrator or CSM of the account to grant you access.
        </p>
        
        
      </div>
    </div>
  );
};

export default AccessDenied;